// The DFT module's defects (D-1): the defect builder - a supercell with the defect's periodic images far enough apart,
// vacancies, substitutions, antisites and interstitials (several make a complex), picked from the host's sites by
// symmetry class (in the table or the 3D view) and its empty places - and the relaxation of the atoms (pw.x relax
// before the SCF; any recipe), and after a run the relaxation's result: steps, energy, forces, how far each atom moved,
// and "continue from the relaxed structure".
import { ActionIcon, Alert, Badge, Button, Checkbox, Grid, Group, List, NumberInput, Radio, ScrollArea, Select, Stack, Table,
  Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { defectBuild, defectCharge, defectElement, defectReference, defectSites, elementOf, opLabel, relaxOf, withRelax, type DefectInfo, type DefectOp,
  type Recipe, type RelaxSettings, type RelaxSummary, type SiteClass } from "../../lib/qe";
import { RecipeStructure } from "./StructureTools";

const ELEMENT = /^[A-Z][a-z]?$/;
const FORCES = [{ value: "0.01", label: "1e-2 (rough)" }, { value: "0.001", label: "1e-3 (usual)" },
  { value: "0.0005", label: "5e-4" }, { value: "0.0001", label: "1e-4 (tight)" }];

/** "1, 2, 5-8" -> [1, 2, 5, 6, 7, 8]; null when it is not such a list. */
export function parseAtoms(text: string, max: number): number[] | null {
  const out: number[] = [];
  for (const part of text.split(/[\s,;]+/).filter(Boolean)) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(part);
    if (!m) return null;
    const a = Number(m[1]), b = m[2] ? Number(m[2]) : a;
    if (a < 1 || b < a || b > max) return null;
    for (let i = a; i <= b; i++) if (!out.includes(i)) out.push(i);
  }
  return out;
}

function Notes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return <Alert color="blue" p="xs"><List size="xs" spacing={2}>{notes.map((n) => <List.Item key={n}>{n}</List.Item>)}</List></Alert>;
}

const shell = (s: Record<string, number>) => Object.entries(s).map(([k, n]) => `${n} ${k}`).join(", ") || "–";

/** The Defects tab: the builder for a host crystal, or what the defect supercell is; and the relaxation. */
export function DefectsPanel({ recipe, onChange, projectId }: { recipe: Recipe; onChange: (r: Recipe) => void; projectId?: string }) {
  const info = recipe.defect as DefectInfo | undefined;
  return (
    <Stack gap="md" data-testid="defects">
      {info ? <DefectSummary info={info} recipe={recipe} onChange={onChange} projectId={projectId} /> : <DefectBuilder recipe={recipe} onChange={onChange} projectId={projectId} />}
      <RelaxPanel recipe={recipe} onChange={onChange} />
    </Stack>
  );
}

function DefectSummary({ info, recipe, onChange, projectId }: {
  info: DefectInfo; recipe: Recipe; onChange: (r: Recipe) => void; projectId?: string;
}) {
  const [notes, setNotes] = useState<string[]>([]);
  const ref = useMutation({ mutationFn: () => defectReference(recipe, projectId), onSuccess: (r) => { setNotes(r.notes); onChange(r.recipe); } });
  const [charge, setCharge] = useState<number>(info.charge ?? 0);
  const chg = useMutation({ mutationFn: () => defectCharge(recipe, charge, projectId), onSuccess: (r) => { setNotes(r.notes); onChange(r.recipe); } });
  const elem = useMutation({ mutationFn: (el: string) => defectElement(recipe, el, projectId), onSuccess: (r) => { setNotes(r.notes); onChange(r.recipe); } });
  const hostEls: string[] = info.host_recipe ? [...new Set<string>((info.host_recipe.structure?.atoms ?? []).map((a: any[]) => elementOf(String(a[0]))))] : [];
  const needed: string[] = [...new Set<string>([...hostEls, ...info.operations.map((o) => o.element).filter((e): e is string => !!e)])].sort();
  if (info.reference)
    return (
      <Stack gap={4} data-testid="defect-summary">
        <Group gap={6}>
          <Text size="sm" fw={600}>Host reference</Text>
          <Badge size="sm" variant="light">{info.atoms_total} atoms</Badge>
          <Badge size="sm" variant="light" color="gray">{info.cells}-fold supercell of {info.host}</Badge>
        </Group>
        <Text size="xs">The perfect supercell, with the run settings of the defect: once it has run, the defect's run shows its levels
          against these band edges (Defect levels). Run it once for every defect in this supercell.</Text>
        <Notes notes={notes} />
      </Stack>
    );
  const sh = info.shells ?? [];
  return (
    <Stack gap={4} data-testid="defect-summary">
      <Group gap={6}>
        <Text size="sm" fw={600}>Defect {info.label}</Text>
        <Badge size="sm" variant="light">{info.atoms_total} atoms</Badge>
        <Badge size="sm" variant="light" color="gray">{info.cells}-fold supercell of {info.host}</Badge>
        {info.spin && <Badge size="sm" variant="light" color="grape">spin{info.spin.moment != null ? ` ${info.spin.moment} μB` : ""}</Badge>}
        {info.distortion && <Badge size="sm" variant="light" color="orange">symmetry broken</Badge>}
        {!!info.charge && <Badge size="sm" variant="light" color="cyan">q = {info.charge > 0 ? "+" : ""}{info.charge}</Badge>}
      </Group>
      <Text size="xs">Periodic images {info.image_distance_A.toFixed(2)} Å apart{info.center ? <> · centre at ({info.center.map((x) => x.toFixed(3)).join(", ")})</> : null}
        {info.atoms.length > 0 && <> · defect atom{info.atoms.length > 1 ? "s" : ""} {info.atoms.join(", ")}</>}
        {info.vacancies.length > 0 && <> · {info.vacancies.length} vacan{info.vacancies.length > 1 ? "cies" : "cy"}</>}
        {sh.length > 0 && <> · nearest neighbours {sh[0].join(", ")}{sh[1] ? ` (then ${sh[1].length} more)` : ""}</>}</Text>
      {info.spin && <Text size="xs">Spins: collinear, starting moment {info.spin.start} on atoms {info.spin.atoms.join(", ")}
        {" "}({Object.entries(info.spin.species).map(([a, b]) => `${a} → ${b}`).join(", ")}){info.spin.moment != null
          ? `; total moment fixed at ${info.spin.moment} μB` : "; total moment free"}.</Text>}
      {info.distortion && <Text size="xs">Symmetry broken: atoms {info.distortion.atoms.join(", ")} moved {info.distortion.amplitude_A} Å
        at random (seed {info.distortion.seed}) before the relaxation.</Text>}
      <Text size="xs" c="dimmed">Made by the defect builder from the host crystal. For another defect, start again from the host
        (its recipe, or a run of it in the project's runs: "Start from this run").</Text>
      <Group gap="xs" mt={4}>
        <Tooltip label={info.host_recipe ? "The perfect supercell with this run's settings" : "Made before the host's recipe was kept: build the reference from the host crystal"}>
          <Button size="xs" variant="light" onClick={() => ref.mutate()} loading={ref.isPending} disabled={!info.host_recipe}
            data-testid="make-reference">Make the host reference</Button>
        </Tooltip>
        <Text size="xs" c="dimmed">for the defect's levels against the host's band edges: it replaces this recipe here (the defect's recipe
          stays with its run), run it once per supercell</Text>
      </Group>
      <ErrorBox error={ref.error} />
      <Text size="sm" fw={600} mt="xs">Formation energy (charge states and chemical potentials)</Text>
      <Group gap="xs" align="flex-end">
        <NumberInput size="xs" w={130} label="Charge state" value={charge} min={-4} max={4} allowDecimal={false}
          onChange={(v) => typeof v === "number" && setCharge(v)} data-testid="charge-input" />
        <Button size="xs" variant="light" onClick={() => chg.mutate()} loading={chg.isPending}
          disabled={charge === (info.charge ?? 0)} data-testid="make-charge">Make this charge state</Button>
        <Text size="xs" c="dimmed" maw={420}>+1: one electron fewer. Each charge state is relaxed on its own; it replaces this recipe here.</Text>
      </Group>
      {needed.length > 0 && <Group gap="xs" data-testid="element-refs">
        <Text size="xs">Chemical potentials: the elements' references</Text>
        {needed.map((el) => <Button key={el} size="compact-xs" variant="default" onClick={() => elem.mutate(el)}
          loading={elem.isPending && elem.variables === el} aria-label={`reference for ${el}`}>{el}</Button>)}
        <Text size="xs" c="dimmed">run each once (with this run's settings); any run of one element in the project counts</Text>
      </Group>}
      <ErrorBox error={chg.error ?? elem.error} />
    </Stack>
  );
}

// == the builder =========================================================================================================
function DefectBuilder({ recipe, onChange, projectId }: { recipe: Recipe; onChange: (r: Recipe) => void; projectId?: string }) {
  const [distance, setDistance] = useState(10);
  const [debounced] = useDebouncedValue(distance, 600);
  const key = JSON.stringify([recipe.structure ?? null, Object.keys(recipe.species ?? {})]);
  const q = useQuery({ queryKey: ["dft-defect-sites", key, debounced], queryFn: () => defectSites(recipe, debounced),
    staleTime: 300_000, placeholderData: (p) => p, retry: false });
  const data = q.data;
  const [cellChoice, setCellChoice] = useState<string | null>(null);
  const options = data?.supercells.options ?? [];
  const chosen = options.find((o) => JSON.stringify(o.matrix) === cellChoice) ?? data?.supercells.recommended;
  const [ops, setOps] = useState<DefectOp[]>([]);
  const [element, setElement] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<number | null>(null);           // a host atom (0-based), from the 3D view
  const [relax, setRelax] = useState(true);
  const [radius, setRadius] = useState<number | null>(null);
  const [spinOn, setSpinOn] = useState(false);
  const [spinWhere, setSpinWhere] = useState<"shell" | "defect">("shell");
  const [moment, setMoment] = useState<number | null>(null);
  const [distort, setDistort] = useState(false);
  const [amplitude, setAmplitude] = useState(0.1);
  const [bcharge, setBcharge] = useState(0);
  const [notes, setNotes] = useState<string[]>([]);
  const classes = data?.sites.classes ?? [];
  const hostElements = useMemo(() => [...new Set(classes.map((c) => c.element))], [classes]);
  const labelOf = (op: DefectOp) => opLabel(op, op.site != null ? elementOf(classes.find((c) => c.atoms.includes(op.site!))?.species ?? "") : null);
  const build = useMutation({
    mutationFn: () => defectBuild(recipe, chosen!.matrix, ops, relax ? (radius ? { free_radius: radius } : {}) : null, projectId, {
      spin: spinOn ? { on: spinWhere, ...(moment != null ? { moment } : {}) } : null,
      distort: relax && distort ? { amplitude } : null, charge: bcharge }),
    onSuccess: (r) => { setNotes(r.notes); setOps([]); onChange(r.recipe); },
  });
  const add = (op: DefectOp) => setOps((o) => [...o, op]);
  const el = (id: string) => (element[id] ?? "").trim();
  const pickedClass = picked == null ? null : classes.find((c) => c.atoms.includes(picked + 1)) ?? null;
  const atomsAfter = chosen ? chosen.atoms - ops.filter((o) => o.kind === "vacancy").length + ops.filter((o) => o.kind === "interstitial").length : 0;
  return (
    <Stack gap="sm" data-testid="defect-builder">
      <Text size="xs" c="dimmed">A point defect in this crystal: the cell is repeated until the defect's periodic images are far enough
        apart, then atoms are taken away (vacancy), replaced (substitution; antisite: by another element of the host) or added
        (interstitial). Several make a complex: each next one sits next to the first. One atom of a symmetry class (Wyckoff
        position) stands for all of them; pick a class in the table or click an atom in the 3D view.</Text>
      <ErrorBox error={q.error} />
      <Group gap="xs" align="flex-end">
        <NumberInput size="xs" w={190} label="Periodic images at least (Å)" value={distance} min={4} max={30} step={1}
          onChange={(v) => typeof v === "number" && v >= 4 && v <= 30 && setDistance(v)} />
        {data && <>
          <Badge variant="light">{data.sites.spacegroup} ({data.sites.number})</Badge>
          <Badge variant="light" color={data.sites.dimensionality === "3D" ? "blue" : "grape"}>{data.sites.dimensionality}</Badge>
          <Text size="xs" c="dimmed">{data.sites.atoms} atoms in the cell; its own images {data.supercells.host_image_distance_A.toFixed(2)} Å apart</Text>
        </>}
        {q.isFetching && <Text size="xs" c="dimmed">finding the supercells …</Text>}
      </Group>

      {data && (
        <>
          <Text size="sm" fw={600}>Supercell</Text>
          <ScrollArea.Autosize mah={220}>
            <Radio.Group value={chosen ? JSON.stringify(chosen.matrix) : null} onChange={setCellChoice}>
              <Table fz="xs" verticalSpacing={2} withRowBorders={false} data-testid="supercells">
                <Table.Thead><Table.Tr><Table.Th /><Table.Th>Cell</Table.Th><Table.Th>Atoms</Table.Th><Table.Th>Images apart</Table.Th>
                  <Table.Th>Lengths (Å)</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {options.map((o) => (
                    <Table.Tr key={JSON.stringify(o.matrix)} c={o.over_limit ? "dimmed" : undefined}>
                      <Table.Td><Radio size="xs" value={JSON.stringify(o.matrix)} disabled={o.over_limit} aria-label={`supercell ${o.label}`} /></Table.Td>
                      <Table.Td>{o.label}{o.recommended && <Badge ml={6} size="xs" color="teal" variant="light">recommended</Badge>}
                        {o.over_limit && <Tooltip label={`Your plan runs up to ${data.supercells.max_atoms} atoms`}>
                          <Badge ml={6} size="xs" color="gray" variant="light">over the plan</Badge></Tooltip>}</Table.Td>
                      <Table.Td>{o.atoms}</Table.Td>
                      <Table.Td c={o.image_distance_A < data.supercells.min_distance_A ? "orange" : undefined}>{o.image_distance_A.toFixed(2)} Å</Table.Td>
                      <Table.Td>{o.lengths_A.map((x) => x.toFixed(2)).join(" × ")}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Radio.Group>
          </ScrollArea.Autosize>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="xs">
                <Text size="sm" fw={600}>Sites of the host</Text>
                <Table fz="xs" verticalSpacing={3} withRowBorders={false} data-testid="defect-sites">
                  <Table.Thead><Table.Tr><Table.Th>Site</Table.Th><Table.Th>Atoms</Table.Th><Table.Th>Neighbours</Table.Th><Table.Th>Defect</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {classes.map((c) => (
                      <SiteRow key={c.id} c={c} active={pickedClass?.id === c.id} hostElements={hostElements} element={el(c.id)}
                        setElement={(v) => setElement((m) => ({ ...m, [c.id]: v }))}
                        onPick={() => setPicked(c.representative - 1)}
                        add={(kind, e) => add({ kind, site: c.representative, ...(e ? { element: e } : {}) })} />
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="sm" fw={600} mt="xs">Empty places (interstitial sites)</Text>
                {data.interstitials.length === 0 ? <Text size="xs" c="dimmed">No empty place of at least 0.9 Å here.</Text> : (
                  <Table fz="xs" verticalSpacing={3} withRowBorders={false} data-testid="interstitials">
                    <Table.Thead><Table.Tr><Table.Th>Place</Table.Th><Table.Th>Free radius</Table.Th><Table.Th>Neighbours</Table.Th><Table.Th>Add</Table.Th></Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {data.interstitials.map((v) => (
                        <Table.Tr key={v.id}>
                          <Table.Td>{v.kind}{v.saddle && <Text span size="xs" c="dimmed"> (between holes)</Text>}
                            <Text size="xs" c="dimmed">({v.frac.map((x) => +x.toFixed(4)).join(", ")}) · {v.count} in the cell</Text></Table.Td>
                          <Table.Td>{v.radius_A.toFixed(2)} Å</Table.Td>
                          <Table.Td>{shell(v.neighbours)}</Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              <TextInput size="xs" w={52} placeholder="H" value={element[v.id] ?? ""} aria-label={`element at ${v.id}`}
                                onChange={(e) => setElement((m) => ({ ...m, [v.id]: e.currentTarget.value }))} />
                              <Button size="compact-xs" variant="default" disabled={!ELEMENT.test(el(v.id))}
                                onClick={() => add({ kind: "interstitial", element: el(v.id), position: v.frac })}>Add</Button>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <RecipeStructure recipe={recipe} hideInfo picked={picked} onPick={setPicked} />
              {pickedClass && <Text size="xs" data-testid="picked-site">Atom {picked! + 1}: {pickedClass.species}, Wyckoff {pickedClass.wyckoff}
                {" "}({pickedClass.count} such atom{pickedClass.count > 1 ? "s" : ""} in the cell)</Text>}
            </Grid.Col>
          </Grid>

          <Stack gap={4}>
            <Text size="sm" fw={600}>The defect</Text>
            {ops.length === 0 ? <Text size="xs" c="dimmed">Nothing chosen yet: add a vacancy, substitution, antisite or interstitial above.</Text> : (
              <Group gap={6} data-testid="defect-ops">
                {ops.map((o, i) => (
                  <Badge key={i} variant="light" size="lg" rightSection={
                    <ActionIcon size="xs" variant="transparent" aria-label={`remove ${labelOf(o)}`}
                      onClick={() => setOps((x) => x.filter((_, k) => k !== i))}>×</ActionIcon>}>{labelOf(o)}</Badge>
                ))}
                {ops.length > 1 && <Text size="xs" c="dimmed">a complex: each next one next to the first</Text>}
              </Group>
            )}
            <Group gap="md" align="flex-end">
              <Checkbox size="xs" checked={relax} onChange={(e) => setRelax(e.currentTarget.checked)}
                label="Relax the atoms around it (recommended)" />
              {relax && <NumberInput size="xs" w={210} label="Hold atoms fixed beyond (Å, optional)" placeholder="all atoms move"
                value={radius ?? ""} min={2} max={30} step={0.5}
                onChange={(v) => setRadius(typeof v === "number" && v > 0 ? v : null)} />}
            </Group>
            <Group gap="md" align="flex-end">
              <Checkbox size="xs" checked={spinOn} onChange={(e) => setSpinOn(e.currentTarget.checked)} data-testid="defect-spin"
                label="Spin-polarised (a moment on the defect)" />
              {spinOn && <>
                <Select size="xs" w={210} label="Starting moment on" allowDeselect={false} value={spinWhere}
                  onChange={(v) => v && setSpinWhere(v as "shell" | "defect")}
                  data={[{ value: "shell", label: "the defect and its neighbours" }, { value: "defect", label: "the defect's own atoms" }]} />
                <NumberInput size="xs" w={190} label="Total moment (μB, optional)" placeholder="free" value={moment ?? ""}
                  min={0} max={20} step={1} onChange={(v) => setMoment(typeof v === "number" ? v : null)} />
              </>}
            </Group>
            <Group gap="md" align="flex-end">
              <Tooltip disabled={relax} label="Needs the relaxation">
                <Checkbox size="xs" checked={relax && distort} disabled={!relax} onChange={(e) => setDistort(e.currentTarget.checked)}
                  data-testid="defect-distort" label="Break the symmetry (Jahn-Teller): move the defect's neighbours a little at random" />
              </Tooltip>
              {relax && distort && <NumberInput size="xs" w={150} label="By (Å)" value={amplitude} min={0.01} max={0.5} step={0.05}
                decimalScale={3} onChange={(v) => typeof v === "number" && setAmplitude(v)} />}
              <NumberInput size="xs" w={120} label="Charge state" value={bcharge} min={-4} max={4} allowDecimal={false}
                onChange={(v) => typeof v === "number" && setBcharge(v)} data-testid="build-charge" />
            </Group>
            <Group gap="xs">
              <Button size="xs" onClick={() => build.mutate()} loading={build.isPending} disabled={!ops.length || !chosen || chosen.over_limit}
                data-testid="make-defect">Make the defect supercell{chosen && ops.length ? ` (${atomsAfter} atoms)` : ""}</Button>
              {ops.length > 0 && <Button size="xs" variant="subtle" onClick={() => setOps([])}>Clear</Button>}
            </Group>
            <ErrorBox error={build.error} />
            <Notes notes={notes} />
          </Stack>
        </>
      )}
    </Stack>
  );
}

function SiteRow({ c, active, hostElements, element, setElement, onPick, add }: {
  c: SiteClass; active: boolean; hostElements: string[]; element: string; setElement: (v: string) => void; onPick: () => void;
  add: (kind: DefectOp["kind"], element?: string) => void;
}) {
  const others = hostElements.filter((e) => e !== c.element);
  return (
    <Table.Tr bg={active ? "var(--mantine-color-blue-light)" : undefined} onClick={onPick} style={{ cursor: "pointer" }}>
      <Table.Td><b>{c.species}</b> {c.wyckoff}<Text size="xs" c="dimmed">e.g. atom {c.representative}</Text></Table.Td>
      <Table.Td>{c.count}</Table.Td>
      <Table.Td>{shell(c.neighbours.species)}{c.neighbours.distance_A != null && <Text size="xs" c="dimmed">at {c.neighbours.distance_A.toFixed(3)} Å</Text>}</Table.Td>
      <Table.Td onClick={(e) => e.stopPropagation()}>
        <Group gap={4} wrap="nowrap">
          <Button size="compact-xs" variant="default" onClick={() => add("vacancy")} aria-label={`vacancy on ${c.id}`}>Vacancy</Button>
          <TextInput size="xs" w={52} placeholder="C" value={element} aria-label={`element for ${c.id}`}
            onChange={(e) => setElement(e.currentTarget.value)} />
          <Button size="compact-xs" variant="default" disabled={!ELEMENT.test(element) || element === c.element}
            onClick={() => add(others.includes(element) ? "antisite" : "substitution", element)} aria-label={`substitute on ${c.id}`}>Substitute</Button>
          {others.map((o) => (
            <Tooltip key={o} label={`Antisite: ${o} on this ${c.element} site`}>
              <Button size="compact-xs" variant="light" onClick={() => add("antisite", o)} aria-label={`antisite ${o} on ${c.id}`}>{o}<sub>{c.element}</sub></Button>
            </Tooltip>
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

// == the relaxation (any recipe) =========================================================================================
export function RelaxPanel({ recipe, onChange }: { recipe: Recipe; onChange: (r: Recipe) => void }) {
  const rel = relaxOf(recipe);
  const nat = (recipe.structure?.atoms ?? []).length;
  const isDefect = !!recipe.defect;
  const set = (patch: Partial<RelaxSettings> | null) => {
    if (patch === null) return onChange(withRelax(recipe, null));
    const next: RelaxSettings = { ...(rel ?? {}), ...patch };
    for (const k of Object.keys(next) as (keyof RelaxSettings)[]) if (next[k] == null) delete next[k];
    if (next.free_radius && !next.center && isDefect) next.center = (recipe.defect as DefectInfo).center ?? undefined;
    if (!next.free_radius) delete next.center;
    onChange(withRelax(recipe, next));
  };
  const [fixedText, setFixedText] = useState((rel?.fixed ?? []).join(", "));
  const fixed = parseAtoms(fixedText, nat);
  return (
    <Stack gap={6} data-testid="relax-panel">
      <Text size="sm" fw={600}>Relaxation</Text>
      <Checkbox size="xs" checked={!!rel} onChange={(e) => set(e.currentTarget.checked ? {} : null)}
        label="Relax the atoms before the SCF (pw.x relax: BFGS, the cell kept as it is)" />
      <Text size="xs" c="dimmed">The atoms move until every force is below the threshold; the SCF, bands and density of states then use the
        relaxed positions, and the run shows how far each atom moved. A relaxation takes about as long as 5-20 SCF runs.</Text>
      {rel && (
        <Group gap="xs" align="flex-end">
          <Select size="xs" w={170} label="Forces below (Ry/bohr)" data={FORCES} allowDeselect={false}
            value={String(rel.forc_conv_thr ?? 0.001)} onChange={(v) => v && set({ forc_conv_thr: Number(v) })} />
          <NumberInput size="xs" w={110} label="At most steps" value={rel.nstep ?? 50} min={1} max={300} allowDecimal={false}
            onChange={(v) => typeof v === "number" && set({ nstep: v })} />
          <Tooltip disabled={isDefect || !!rel.center} label="Needs a centre: made by the defect builder">
            <NumberInput size="xs" w={190} label="Hold atoms beyond (Å)" placeholder="none" disabled={!isDefect && !rel.center}
              value={rel.free_radius ?? ""} min={1} max={50} step={0.5}
              onChange={(v) => set({ free_radius: typeof v === "number" && v > 0 ? v : undefined })} />
          </Tooltip>
          <TextInput size="xs" w={170} label="Hold these atoms (numbers)" placeholder="e.g. 1, 5-8" value={fixedText}
            error={fixed === null ? `atom numbers 1 … ${nat}` : undefined}
            onChange={(e) => setFixedText(e.currentTarget.value)}
            onBlur={() => fixed !== null && JSON.stringify(fixed) !== JSON.stringify(rel.fixed ?? []) && set({ fixed: fixed.length ? fixed : undefined })} />
        </Group>
      )}
    </Stack>
  );
}

// == after a run =========================================================================================================
export function RelaxResult({ relax, onContinue }: { relax: RelaxSummary; onContinue?: () => void }) {
  return (
    <Stack gap="sm" data-testid="relax-result">
      <Group gap={6}>
        <Badge color={relax.converged ? "teal" : "orange"} variant="light">{relax.converged ? "converged" : "not converged"}</Badge>
        <Text size="sm">{relax.bfgs_steps} BFGS steps ({relax.scf_cycles} SCF runs) · energy {relax.energy_change_eV >= 0 ? "+" : ""}
          {relax.energy_change_eV.toFixed(4)} eV · last total force {relax.total_force_Ry_bohr ?? "?"} Ry/bohr
          {relax.forc_conv_thr != null ? ` (each atom below ${relax.forc_conv_thr})` : ""}</Text>
      </Group>
      <Text size="xs">{relax.atoms} atoms{relax.fixed_atoms ? `, ${relax.fixed_atoms} held fixed` : ""} · the largest move
        {" "}{relax.max_displacement_A.toFixed(3)} Å. The bands and the density of states are of the relaxed structure (its 3D view: Lattice structure).</Text>
      <ScrollArea.Autosize mah={300}>
        <Table fz="xs" verticalSpacing={2} withRowBorders={false} data-testid="displacements">
          <Table.Thead><Table.Tr><Table.Th>Atom</Table.Th><Table.Th>Species</Table.Th><Table.Th>Moved (Å)</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {relax.displacements.map((d) => (
              <Table.Tr key={d.atom}><Table.Td>{d.atom}</Table.Td><Table.Td>{d.species}</Table.Td><Table.Td>{d.d_A.toFixed(4)}</Table.Td></Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
      {onContinue && <Group><Button size="xs" variant="light" onClick={onContinue} data-testid="continue-relaxed">
        Continue from the relaxed structure</Button>
        <Text size="xs" c="dimmed">a new calculation with these positions (no relaxation), e.g. with a density of states or spin</Text></Group>}
    </Stack>
  );
}
